import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {Currency} from 'app/models/currency.model';
import {
  DisplayedComboGroupStructure,
  emptyOverride,
  MenuSection, ModifierGroupStructure, ModifierProductStructure, ModifierProductStructureOverride,
  OverrideInstruction,
  OverridePosition,
  OverridePositionRequest, OverridePositionsMap,
  PositionTypeEnum, Product
} from 'app/models/menu.model';
import {
  EditProductModalComponent,
  EditProductsModalComponentDataType
} from 'app/pages/menu/shared/edit-product-modal/edit-product-modal.component';
import {MatDialog} from '@angular/material/dialog';
import {MenuOverrideService} from 'app/services/menu-override.service';
import {Organization} from 'app/models/organization.model';
import {MenuService} from 'app/services/menu.service';
import {RoleMembership, selectRole} from "app/reducers/context.selectors";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {Store} from "@ngrx/store";
import {ModifierInitValues} from "app/models/modifierInitValues.model";
import {ChangeIsHiddenEvent} from "app/models/changeIsHiddenEvent.model";
import { ProductApiService } from 'app/services/product-api.service';

@UntilDestroy()
@Component({
  selector: 'app-edit-modifier[modifierGroup][originalModifierGroup][editedModifierId][saveForAll][saveForAllCombo]',
  templateUrl: './edit-modifier.component.html',
  styleUrls: ['./edit-modifier.component.scss']
})
export class EditModifierComponent implements OnInit {
  isBeingSaveProcessed = false;
  isEditingMode = false;
  editModifierIconTooltip: string;
  editProductIconTooltip: string;

  @Input() allowOverrides: boolean;
  @Input() modifierProduct: ModifierProductStructure | null;
  @Input() originalModifierProduct: ModifierProductStructure | null;
  @Input() currency: Currency | null;
  @Input() dictionaryProducts: Map<string, Product>;
  @Input() originalDictionaryProducts: Map<string, Product>;
  @Input() overridePositionsMap: OverridePositionsMap;
  @Input() currentOrganization: Organization;
  @Input() menuSections: MenuSection[];
  @Input() menuSectionId: string;
  @Input() productId: string;
  @Input() saveForMenuSections: string[];
  @Input() saveForAllCombo: boolean;
  @Input() saveForAll: boolean;
  // Чтобы можно было одновременно редактировать min-max только у одного модификатора группы.
  @Input() editedModifierId: string;
  @Output() editedModifierIdChange = new EventEmitter<string>();
  @Input() originalModifierGroup: ModifierGroupStructure;
  @Input() modifierGroup: ModifierGroupStructure;
  @Input() index: number;
  @Input() isHidden: boolean;
  @Input() comboGroupStructure?: DisplayedComboGroupStructure;
  @Input() saveForAllProductsInComboGroup: boolean;
  @Output() changeIsHidden = new EventEmitter<ChangeIsHiddenEvent>();
  @Output() changeInformationMessageEvent = new EventEmitter<string[]>();

  changes = [];

  modifierName: string;
  modifierMin: number;
  modifierMax: number;
  modifierDefaultAmount: number;
  modifierFreeQuantity: number;
  initValues: ModifierInitValues = {};

  public roleMembership: RoleMembership | undefined = undefined;

  constructor(
    public dialog: MatDialog,
    private translate: TranslateService,
    private menuService: MenuService,
    public store: Store,
    private productApiService: ProductApiService,
  ) {
  }

  ngOnInit(): void {
    this.modifierName = this.getProductName(this.modifierProduct.modifierId);
    this.modifierMin = this.modifierProduct.min;
    this.modifierMax = this.modifierProduct.max;
    this.modifierDefaultAmount = this.modifierProduct.defaultAmount;
    this.modifierFreeQuantity = this.modifierProduct.freeQuantity ?? 0;
    this.setInitValues();
    this.editModifierIconTooltip = this.translate.instant('MENU.EDIT_MODIFIER');
    this.editProductIconTooltip = this.translate.instant('MENU.EDIT_PRODUCT');

    this.store.select(selectRole).pipe(
      untilDestroyed(this)
    ).subscribe(roleMembership => {
      this.roleMembership = roleMembership;
    });
  }

  private setInitValues() {
    this.initValues.min= this.modifierMin;
    this.initValues.max = this.modifierMax;
    this.initValues.defaultAmount = this.modifierDefaultAmount;
    this.initValues.freeQuantity = this.modifierFreeQuantity;
    this.initValues.isHidden = this.isHidden;
  }

  getProductName(productId: string) {
    return this.dictionaryProducts.get(productId).name;
  }

  getProductPrice(productId: string) {
    return this.dictionaryProducts.get(productId).productStructure.price;
  }

  async editModifier() {
    if(!this.allowOverrides) {
      return;
    }
    if (this.isEditingMode) {
      if (this.validate()) {
        await this.saveModifierStructure();
        this.toggleEditMode();
        this.setInitValues();
      }
    } else {
      this.toggleEditMode();
    }
  }

  toggleEditMode() {
    this.editedModifierId = this.isEditingMode? '' : this.modifierProduct.modifierId;

    this.isEditingMode = !this.isEditingMode;
    this.editModifierIconTooltip = this.translate.instant(this.isEditingMode ? 'COMMON.SAVE' : 'MENU.EDIT_MODIFIER');

    this.editedModifierIdChange.emit(this.editedModifierId);
  }

  isEditHidden(){
    return this.editedModifierId && this.editedModifierId !== this.modifierProduct.modifierId;
  }

  async saveModifierStructure() {
    this.isBeingSaveProcessed = true;
    let productIds: string[] = [this.productId];
    if (this.saveForAll || this.saveForAllCombo){
      const externalProductIdsResponse = await this.productApiService.getExternalProductIdsByModifierGroup(
        this.menuSections[0].menuId,
        this.modifierGroup.modifierGroupId,
        this.saveForAllCombo);

      productIds = externalProductIdsResponse.data ?? [];
    }

    if(this.saveForAllProductsInComboGroup && this.comboGroupStructure){
      this.comboGroupStructure.products.forEach(product => {
        if(!product.isHidden && product.externalProductId !== this.productId){
          productIds.push(product.externalProductId)
        }
      });
    }

    const overridePositionRequest: OverridePositionRequest[] = [];

    for (let externalProductId of productIds) {
      const itemOverridePositionRequests =
        this.buildOverridePositionRequest(externalProductId, this.modifierProduct.modifierId);
      itemOverridePositionRequests.forEach(itemOverridePositionRequest => overridePositionRequest.push(itemOverridePositionRequest))
    }

    if (overridePositionRequest.length > 0) {
      await this.menuService.saveOverridePositions(this.menuSectionId, overridePositionRequest, this.saveForMenuSections);
    }

    this.isBeingSaveProcessed = false;
  }

  buildOverridePositionRequest(externalProductId: string, modifierId: string): OverridePositionRequest[]
  {
    let isOverrideExists: boolean;
    const positionId = `${externalProductId}_${modifierId}`;
    let productStructureOverridePosition =
      this.overridePositionsMap.getOverride(PositionTypeEnum.ModifierProductStructure, positionId);
    // if override position does not exists create a new one
    if (!productStructureOverridePosition) {
      productStructureOverridePosition = {
        positionType: PositionTypeEnum.ModifierProductStructure,
        positionId: positionId,
        override: emptyOverride,
        menuSectionId: this.menuSectionId,
      };
    } else {
      // if override position exists update it anyway no matter has changes or not
      isOverrideExists = true;
    }

    const overrideInstructions: OverrideInstruction[] = []
    if (this.initValues.min != this.modifierMin) {
      MenuOverrideService.overrideNumberFiled(this.modifierProduct, this.originalModifierProduct, 'min', this.modifierMin, isOverrideExists, overrideInstructions);
    }
    if (this.initValues.max != this.modifierMax) {
      MenuOverrideService.overrideNumberFiled(this.modifierProduct, this.originalModifierProduct, 'max', this.modifierMax, isOverrideExists, overrideInstructions);
    }
    if (this.initValues.defaultAmount != this.modifierDefaultAmount) {
      MenuOverrideService.overrideNumberFiled(this.modifierProduct, this.originalModifierProduct, 'defaultAmount', this.modifierDefaultAmount, isOverrideExists, overrideInstructions);
    }
    if (this.initValues.freeQuantity != this.modifierFreeQuantity) {
      MenuOverrideService.overrideNumberFiled(this.modifierProduct, this.originalModifierProduct, 'freeQuantity', this.modifierFreeQuantity, isOverrideExists, overrideInstructions);
    }

    let overrideModifierGroupIsHidden: OverridePositionRequest;

    if (this.initValues.isHidden != this.isHidden || this.isHiddenWasChanged(productStructureOverridePosition)) {
      this.changeIsHidden.emit({isHidden: this.isHidden, index: this.index});

      MenuOverrideService.overrideBooleanFiled(this.modifierProduct, this.originalModifierProduct, 'isHidden', this.isHidden, isOverrideExists, overrideInstructions);

      if (!this.isHidden && this.modifierGroup.isHidden) {
        overrideModifierGroupIsHidden = this.overrideModifierGroupIsHidden();
      }
    }

    if (overrideInstructions.length > 0) {
      const newProductStructureOverridePosition: OverridePositionRequest = {
        ...productStructureOverridePosition,
        instructions: overrideInstructions
      };
      let overridePositionRequest = [newProductStructureOverridePosition]
      if(overrideModifierGroupIsHidden){
        overridePositionRequest.push(overrideModifierGroupIsHidden)
      }

      return overridePositionRequest;
    }

    return [];
  }

  private isHiddenWasChanged(productStructureOverridePosition: OverridePosition) {
    if (!productStructureOverridePosition.override){
      return false;
    }
    const groupOverride = JSON.parse(productStructureOverridePosition.override) as ModifierProductStructureOverride;
    return !!groupOverride.isHidden != this.isHidden;
  }

  isModifierMinDifferent() {
    return this.originalModifierProduct.min !== this.initValues[ 'min' ] ;
  }

  isModifierMaxDifferent() {
    return this.originalModifierProduct.max !== this.initValues[ 'max' ] ;
  }

  isModifierDefaultAmountDifferent() {
    return this.originalModifierProduct.defaultAmount !== this.initValues[ 'defaultAmount' ] ;
  }

  isModifierFreeQuantityDifferent() {
    return (this.originalModifierProduct.freeQuantity ?? 0) !== (this.initValues[ 'freeQuantity' ] ?? 0);
  }

  isModifierFreeQuantityInputDifferent() {
    return (this.originalModifierProduct.freeQuantity ?? 0) !== this.modifierFreeQuantity;
  }

  isModifierMinInputDifferent() {
    return this.originalModifierProduct.min !== this.modifierMin;
  }

  isModifierMaxInputDifferent() {
    return this.originalModifierProduct.max !== this.modifierMax;
  }

  isModifierNameDifferent() {
    const product = this.dictionaryProducts.get(this.modifierProduct.modifierId);
    const originalProduct = this.originalDictionaryProducts.get(this.modifierProduct.modifierId);
    return product.name !== originalProduct.name;
  }

  editProduct() {
    const product = this.dictionaryProducts.get(this.modifierProduct.modifierId);
    const originalProduct = this.originalDictionaryProducts.get(this.modifierProduct.modifierId);
    this.dialog.open(EditProductModalComponent, {
      data: {
        product,
        originalProduct,
        menuSections: this.menuSections,
        menuSectionId: this.menuSectionId,
        organization: this.currentOrganization,
        organizationId: this.menuSectionId,
        allowOverrides: this.allowOverrides,
      } as EditProductsModalComponentDataType
    });
  }

  onChangeInformationMessage() {
    this.changes = [];
    if (this.initValues.min != this.modifierMin) {
      this.changes.push(`"Modifier ${this.getProductName(this.productId)} Min"`)
    }
    if (this.initValues.max != this.modifierMax) {
      this.changes.push(`"Modifier ${this.getProductName(this.productId)} Max"`)
    }
    if (this.initValues.defaultAmount != this.modifierDefaultAmount) {
      this.changes.push(`"Modifier ${this.getProductName(this.productId)} defaultAmount"`)
    }
    if (this.initValues.freeQuantity != this.modifierFreeQuantity) {
      this.changes.push(`"Modifier ${this.getProductName(this.productId)} Free"`)
    }
    if (this.initValues.isHidden!= this.isHidden) {
      this.changes.push(`"Modifier ${this.getProductName(this.productId)} isHidden"`)
    }

    this.changeInformationMessageEvent.emit(this.changes);
  }

  validate() {
    return this.modifierGroup.childModifiersHaveMinMaxRestrictions ?
      this.validateMin()
        && this.validateMax()
        && this.validateDefault()
        && this.validateFreeQuantity() :
      true;
  }

  private validateMin() {
    if (this.modifierMin < this.originalModifierProduct.min){
      const validationMessage = this.translate.instant('MENU.VALIDATION_MESSAGE.MIN_MODIFIER_LESS_ORIGINAL_MIN');
      alert(validationMessage);
      this.modifierMin = this.originalModifierProduct.min;
      return false;
    }

    if (this.modifierMin > this.modifierMax){
      const validationMessage = this.translate.instant('MENU.VALIDATION_MESSAGE.MIN_GREATER_MAX');
      alert(validationMessage);
      this.modifierMin = this.modifierMax;
      return false;
    }

    return true;
  }

  private validateMax() {
    if (this.originalModifierProduct.max > 0 && this.modifierMax > this.originalModifierProduct.max){
      const validationMessage = this.translate.instant('MENU.VALIDATION_MESSAGE.MAX_MODIFIER_GREATER_ORIGINAL_MAX');
      alert(validationMessage);

      this.modifierMax = this.originalModifierProduct.max;
      return false;
    }

    if(this.modifierGroup.max > 0 && this.modifierMax > this.modifierGroup.max) {
      const validationMessage = this.translate.instant('MENU.VALIDATION_MESSAGE.MAX_MODIFIER_GREATER_MAX_GROUP');
      alert(validationMessage);

      this.modifierMax = this.modifierGroup.max;
      return false;
    }

    return true;
  }

  private validateDefault() {
    if(this.modifierMax > 0 && this.modifierDefaultAmount > this.modifierMax) {
      const validationMessage = this.translate.instant('MENU.VALIDATION_MESSAGE.DEFAULT_MODIFIER_GREATER_MAX');
      alert(validationMessage);

      this.modifierDefaultAmount = this.modifierMax;
      return false;
    }

    if(this.modifierGroup.max > 0 && this.modifierDefaultAmount > this.modifierGroup.max) {
      const validationMessage = this.translate.instant('MENU.VALIDATION_MESSAGE.DEFAULT_MODIFIER_GREATER_MAX_GROUP');
      alert(validationMessage);

      this.modifierDefaultAmount = this.modifierGroup.max;
      return false;
    }

    if(this.modifierDefaultAmount < this.modifierMin) {
      const validationMessage = this.translate.instant('MENU.VALIDATION_MESSAGE.DEFAULT_MODIFIER_LESS_MIN');
      alert(validationMessage);

      this.modifierDefaultAmount = this.modifierMin;
      return false;
    }

    return true;
  }

  private validateFreeQuantity() {
    if (this.modifierFreeQuantity < 0) {
      const validationMessage = this.translate.instant('MENU.VALIDATION_MESSAGE.FREE_QUANTITY_NEGATIVE_MODIFIER');
      alert(validationMessage);

      this.modifierFreeQuantity = 0;
      return false;
    }

    if (this.modifierMax > 0 && this.modifierFreeQuantity > this.modifierMax) {
      const validationMessage = this.translate.instant('MENU.VALIDATION_MESSAGE.FREE_QUANTITY_GREATER_MAX_MODIFIER');
      alert(validationMessage);

      this.modifierFreeQuantity = this.modifierMax;
      return false;
    }

    return true;
  }

  private overrideModifierGroupIsHidden() : OverridePositionRequest {
    const modifierGroupoverrideInstructions: OverrideInstruction[] = []

    let isOverrideExists: boolean;

    let modifierGroupStructureOverridePosition =
      this.overridePositionsMap.getOverride(
        PositionTypeEnum.ModifierGroupStructure, `${this.productId}_${this.modifierGroup.modifierGroupId}`);
    // if override position does not exists create a new one
    if (!modifierGroupStructureOverridePosition) {
      modifierGroupStructureOverridePosition = {
        positionType: PositionTypeEnum.ModifierGroupStructure,
        positionId: `${this.productId}_${this.modifierGroup.modifierGroupId}`,
        override: emptyOverride,
        menuSectionId: this.menuSectionId,
      };
    } else {
      // if override position exists update it anyway no matter has changes or not
      isOverrideExists = true;
    }

    MenuOverrideService.overrideBooleanFiled(
      this.modifierGroup,
      this.originalModifierGroup,
      'isHidden',
      this.isHidden,
      isOverrideExists,
      modifierGroupoverrideInstructions);

    const newModifierGroupStructureOverridePosition: OverridePositionRequest = {
      ...modifierGroupStructureOverridePosition,
      instructions: modifierGroupoverrideInstructions
    };

    return newModifierGroupStructureOverridePosition;
  }
}
